import { node } from "~/util/dom";
import ContentOverlay from "~/content.overlay";
import SharePosition from "./index";
import { store } from "~/state/store";
import { Unsubscribe } from "redux";
import { watch, watchUser } from "~/state/util";
import { selectCurrentUser } from "~/state/user/selectors";
import { fakeLogin, initSharedPositions, loadInvitations, setSEO } from "./utils";
import { CONFIG } from "~/config";
import { ELoginState } from "~/state/user/interfaces";
import { acceptInvitation } from "~/util/providers/share-position/invitations";
import Dialog from "~/ui/dialog";
import { _ } from "~/i18n";
import Scene from "~/scene";
import { setActiveSubscription, setInvitations, setSubscriptions } from "~/state/share-position/state";
import { makeEvent } from "~/lib/signals";

export default class SharePositionContent extends ContentOverlay {
	#sharePosition: SharePosition;

	_loadPromise: Promise<typeof import("~/share-position2/index")>;

	_storeUnsubscribe: Unsubscribe;

	constructor() {
		super();
		this._dom = {
			container: node("div"),
		};
		this._buildCloseButton();
		this.#sharePosition = null;
		this._loadPromise = null;

		const wt = watch(store.getState, "user", watchUser);

		this._storeUnsubscribe = store.subscribe(wt(newUser => {
			if (!CONFIG.inFrame) {
				const { user } = newUser;

				// nejdriv vynulujeme stavajici, protoze pri zmene uzivatele tam nesmi strasit ty toho predchoziho
				store.dispatch(setSubscriptions([]));
				store.dispatch(setInvitations([]));

				// nacteme/zacneme nacitat nova data
				initSharedPositions(user, `.${CONFIG.BASE_DOMAIN.split(":")[0]}`);
				loadInvitations(user);
			}
		}));

		if (!CONFIG.inFrame) {
			const user = selectCurrentUser(store.getState());

			initSharedPositions(user, `.${CONFIG.BASE_DOMAIN.split(":")[0]}`);
			loadInvitations(user);
		}
	}

	destroy() {
		this.#sharePosition?.destroy();

		this._storeUnsubscribe();
	}

	activate() {
		setSEO();
		const thisSuper = super.activate();

		makeEvent("shareposition-activated", this);
		this._load().then(() => {}, () => {});

		return thisSuper;
	}

	deactivate() {
		if (!this.isActive()) {
			return this;
		}

		store.dispatch(setActiveSubscription(""));
		makeEvent("shareposition-deactivated", this);

		return super.deactivate();
	}

	show() {
		Mapy.getComponent("layout").setWidth(1);

		return super.show();
	}

	async setState(state) {
		if (!CONFIG.inFrame && !CONFIG.isScreenshoter && "share-position" in state) {
			const storeState = store.getState();

			try {
				// pokud nejsme prihlaseni, udelame fake prihlaseni
				const user = selectCurrentUser(storeState);

				if (user.state !== ELoginState.Green) {
					await fakeLogin(`.${CONFIG.BASE_DOMAIN.split(":")[0]}`);
				}

				// zacneme sledovat dle invitationId
				const accepted = await acceptInvitation(state["share-position"]);
				const subscriptions = await initSharedPositions(user, `.${CONFIG.BASE_DOMAIN.split(":")[0]}`);
				const addedSubscription = subscriptions.find(subscription => subscription.subscriptionId === accepted.subscriptionId);

				// nove sledovanou rovnou nastavime jako aktivni
				store.dispatch(setActiveSubscription(accepted.subscriptionId));

				Mapy.getComponent("logger").log("sharelocation_open", {
					state: addedSubscription.sharingStatus,
					subscriptionCount: subscriptions.length,
					routeType: addedSubscription.routeParts?.length ? addedSubscription.routeParts[0].routeSettings.criterion : 0,
					count: addedSubscription.routeParts?.length || 0,
					length: addedSubscription.routeInfo?.totalDistance || 0,
					duration: addedSubscription.routeInfo ? Math.round(Math.abs(new Date(addedSubscription.routeInfo.navigationStart).getTime() - new Date(addedSubscription.routeInfo.arrivalTime).getTime()) / (1000 * 60)) : 0,
				});

				// aktivujeme se
				this.activate();
			// pokud to nedopadlo, ukazeme hlasku
			} catch (ex) {
				// eslint-disable-next-line no-console
				console.log(ex);
				let title = _("sharePosition.dialogError.title");
				let desc = _("sharePosition.dialogError.text");
				const status = parseInt(ex.message);

				Mapy.getComponent("logger").log("sharelocation_open", {
					state: "error",
					subscriptionCount: storeState.sharePosition.subscriptions.length,
				});

				// tohle sdileni muze jen prihlaseny, takze presmerujeme na login
				if (status === 403) {
					const url = new URL(`https://login.szn.${CONFIG.TLD}`);
					const returnUrl = new URL((window as Window & typeof globalThis).location.href);

					Array.from(returnUrl.searchParams.keys()).forEach(key => returnUrl.searchParams.delete(key));
					returnUrl.searchParams.set("share-position", state["share-position"]);
					url.searchParams.set("return_url", returnUrl.toString());
					url.searchParams.set("serviceId", "mapy");

					(window as Window & typeof globalThis).location.href = url.toString();
				// neni to zamitnute, protoze prestal sdilet
				} else if (status === 410) {
					title = _("sharePosition.dialogEnded.title");
					desc = _("sharePosition.dialogEnded.text");
				} else if (status === 406) {
					title = _("sharePosition.dialogAlreadyUsed.title");
					desc = _("sharePosition.dialogAlreadyUsed.text");
				}

				const dialog = new Dialog({
					bgClose: false,
					title,
					desc,
					className: "share-position-popup",
				});

				dialog.addBtn({
					title: _("ok"),
					cb: () => {
						dialog.close();
					},
					bindEnter: true,
				});
				dialog.open();
			}
		}
	}

	_load() {
		if (!this._loadPromise) {
			Mapy.getComponent("loader").start(true);
			this._loadPromise = import(
				/* webpackChunkName: "share-position2" */
				"~/share-position2/index"
			);
			this._loadPromise.then(module => {
				module.initModule({ store, CONFIG, Scene, _ });
				// eslint-disable-next-line new-cap
				this.#sharePosition = new module.default();
				this._dom.container.appendChild(this.#sharePosition.container);
			}, () => {}).finally(() => {
				Mapy.getComponent("loader").end(true);
			});
		}

		return this._loadPromise;
	}
}
